import { BookOpen, CalendarClock, Radio, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Markdown } from '../../components/Markdown';
import { Spinner, cx } from '../../components/ui';
import { fetchLivePage, getVoterToken, logScan } from '../../lib/api';
import type { LivePage as LivePageData } from '../../lib/types';
import { brandStyle, formatInZone } from '../../lib/utils';
import { useScreenWakeLock } from '../../lib/wakeLock';
import { FirstTimeModal } from '../../components/FirstTimeModal';
import { ActionView, type ThemeTokens } from './ActionView';
import { NotesHistoryModal } from './NotesHistoryModal';

import { supabase } from '../../lib/supabase';

export const THEMES: Record<'light' | 'dark' | 'brand', ThemeTokens> = {
  light: {
    page: 'bg-slate-100 text-slate-900',
    card: 'bg-white text-slate-900',
    muted: 'text-slate-500',
    input: 'border-slate-200 bg-slate-50 text-slate-900',
    chip: 'bg-slate-100 text-slate-800 hover:bg-slate-200',
    chipActive: 'bg-brand text-white',
  },
  dark: {
    page: 'bg-slate-950 text-white',
    card: 'bg-slate-900 text-white ring-1 ring-white/10',
    muted: 'text-slate-400',
    input: 'border-slate-700 bg-slate-800 text-white',
    chip: 'bg-slate-800 text-slate-100 hover:bg-slate-700',
    chipActive: 'bg-brand text-white',
  },
  brand: {
    page: 'bg-brand text-white',
    card: 'bg-white text-slate-900',
    muted: 'text-slate-500',
    input: 'border-slate-200 bg-slate-50 text-slate-900',
    chip: 'bg-slate-100 text-slate-800 hover:bg-slate-200',
    chipActive: 'bg-white text-brand',
  },
};

const POLL_MS = 15_000;

export default function LivePage() {
  const { churchSlug = '', serviceSlug } = useParams();
  const [search] = useSearchParams();
  const previewAt = search.get('at') || undefined;
  const isEmbedPreview = search.has('preview');

  const [data, setData] = useState<LivePageData | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');
  const [offline, setOffline] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Keep screen awake while attendee is following along with live service
  useScreenWakeLock(!isEmbedPreview);

  const load = useCallback(async (bypassCache = false) => {
    try {
      const page = await fetchLivePage(churchSlug, serviceSlug, previewAt, bypassCache);
      if (!page) {
        setStatus('notfound');
        return;
      }
      setData(page);
      setStatus('ready');
      setOffline(false);
    } catch {
      setOffline(true);
      setStatus((s) => (s === 'loading' ? 'error' : s));
    }
  }, [churchSlug, serviceSlug, previewAt]);

  // Real-time updates via Supabase WebSocket (channel listening to table changes)
  useEffect(() => {
    if (isEmbedPreview || !churchSlug) return;

    // Supabase Realtime channel for instant reactivity (<200ms) and attendee presence
    const channel = supabase
      .channel(`live:${churchSlug}`, {
        config: { presence: { key: getVoterToken() } },
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'actions' }, () => {
        load(true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'services' }, () => {
        load(true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'churches' }, () => {
        load(true);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && !isEmbedPreview) {
          try {
            await channel.track({
              online_at: new Date().toISOString(),
              device: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            });
          } catch {
            /* ignore */
          }
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [churchSlug, isEmbedPreview, load]);

  // Log QR scan telemetry if arrived via QR code
  useEffect(() => {
    if (isEmbedPreview || !churchSlug) return;
    const params = new URLSearchParams(window.location.search);
    const src = params.get('src') || params.get('ref') || params.get('source');
    if (src === 'qr') {
      logScan(churchSlug, serviceSlug, 'qr');
    }
  }, [churchSlug, serviceSlug, isEmbedPreview]);

  // Poll for live changes as fallback (disabled inside admin iframe preview)
  useEffect(() => {
    load();
    if (isEmbedPreview) return;
    const id = setInterval(() => load(false), POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && load(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', () => load(true));
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', () => load(true));
    };
  }, [load, isEmbedPreview]);

  // Re-fetch when the earliest upcoming action window ends
  useEffect(() => {
    clearTimeout(timer.current);
    if (isEmbedPreview || previewAt) return;
    const now = Date.now();
    const futureExpirations = (data?.actions || [])
      .map((a) => (a.visible_until ? new Date(a.visible_until).getTime() : Infinity))
      .filter((t) => Number.isFinite(t) && t > now);
    if (futureExpirations.length > 0) {
      const nextExpiry = Math.min(...futureExpirations);
      const ms = Math.max(1000, nextExpiry - now + 500);
      if (ms < POLL_MS) {
        timer.current = setTimeout(() => load(true), ms);
      }
    }
    return () => clearTimeout(timer.current);
  }, [data, load, previewAt, isEmbedPreview]);

  const landing = data?.church.landing || {};
  const theme = THEMES[landing.theme || 'light'];

  const actions = data?.actions || [];
  const primary = useMemo(
    () => actions.find((a) => a.id === selectedId) || actions[0] || null,
    [actions, selectedId],
  );

  // A newly pinned action always wins attention
  const pinnedId = actions.find((a) => a.pinned)?.id;
  useEffect(() => {
    if (pinnedId) setSelectedId(pinnedId);
  }, [pinnedId]);

  useEffect(() => {
    if (!data) return;
    document.title = primary ? `${primary.title} · ${data.church.name}` : data.church.name;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', data.church.primary_color);
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', data.church.name);
  }, [data, primary]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Spinner />
      </div>
    );
  }

  if (status === 'notfound' || (status === 'error' && !data)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-slate-100 p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800">{status === 'notfound' ? 'Page not found' : 'We could not load this page'}</h1>
        <p className="text-slate-500">{status === 'notfound' ? 'Please check the QR code or link.' : 'Check your connection and try again.'}</p>
        {status === 'error' && (
          <button onClick={() => load(true)} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">
            Retry
          </button>
        )}
      </div>
    );
  }

  const church = data!.church;

  return (
    <div className={cx('min-h-screen pb-[env(safe-area-inset-bottom)]', theme.page)} style={brandStyle(church.primary_color, church.accent_color)}>
      <div className="mx-auto flex min-h-screen max-w-xl flex-col px-4 pb-8 pt-[max(1rem,env(safe-area-inset-top))]">
        {/* Sticky Header with Backdrop Blur */}
        <header
          className={cx(
            'sticky top-0 z-30 -mx-4 mb-3 flex items-center justify-between gap-3 px-4 py-2.5 backdrop-blur-md transition-colors border-b',
            landing.theme === 'brand'
              ? 'bg-brand/90 text-white border-white/10 shadow-xs'
              : theme.page.includes('slate-950')
              ? 'bg-slate-950/90 text-white border-white/10 shadow-xs'
              : 'bg-slate-100/90 text-slate-900 border-black/5 shadow-xs'
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {church.logo_url ? (
              <img src={church.logo_url} alt={church.name} className="h-10 w-10 shrink-0 rounded-xl bg-white object-contain p-1" />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/90 text-lg font-bold text-brand">
                {church.name.charAt(0)}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold leading-tight">{church.name}</p>
              {data!.live && data!.service && landing.show_service_name !== false && (
                <p className="truncate text-xs opacity-75">{data!.service.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setNotesModalOpen(true)}
              className={cx(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center gap-1.5 rounded-full text-xs font-semibold shadow-sm transition active:scale-95 min-[475px]:h-auto min-[475px]:w-auto min-[475px]:px-3 min-[475px]:py-1.5',
                landing.theme === 'brand' ? 'bg-white/15 text-white hover:bg-white/25' : theme.chip
              )}
              title="Search and view your saved sermon notes"
              aria-label="My Notes"
            >
              <BookOpen className="h-4 w-4 shrink-0 min-[475px]:h-3.5 min-[475px]:w-3.5" />
              <span className="hidden min-[475px]:inline">My Notes</span>
            </button>

            {data!.live && (
              <span
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full p-1.5 text-xs font-semibold min-[475px]:bg-red-500 min-[475px]:px-2.5 min-[475px]:py-1 min-[475px]:text-white"
                title="Live service in progress"
                aria-label="Live service"
              >
                <span className="relative flex h-2.5 w-2.5 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75 min-[475px]:hidden" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 min-[475px]:hidden" />
                  <Radio className="hidden h-3 w-3 animate-pulse min-[475px]:block" />
                </span>
                <span className="hidden min-[475px]:inline">LIVE</span>
              </span>
            )}
          </div>
        </header>

        {offline && (
          <div className="mb-3 flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 text-sm text-amber-900">
            <WifiOff className="h-4 w-4" /> Connection lost — showing the latest content.
          </div>
        )}

        {previewAt && !isEmbedPreview && (
          <div className="mb-3 rounded-xl bg-indigo-100 px-3 py-2 text-sm text-indigo-900">
            Preview at {formatInZone(previewAt, church.timezone, { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        )}

        {/* Action switcher when several actions are live simultaneously */}
        {actions.length > 1 && (
          <nav className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1" aria-label="Live items">
            {actions.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedId(a.id)}
                className={cx(
                  'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition',
                  primary?.id === a.id ? theme.chipActive : landing.theme === 'brand' ? 'bg-white/15 text-white' : theme.chip,
                )}
              >
                {a.title}
              </button>
            ))}
          </nav>
        )}

        <main className="flex-1">
          {primary ? (
            <div key={primary.id} className="animate-fade-up">
              <ActionView
                action={primary}
                theme={theme}
                preview={Boolean(previewAt)}
                churchSlug={church.slug}
                churchName={church.name}
                serviceName={data!.service?.name}
                churchLanding={church.landing}
              />
            </div>
          ) : (
            <Idle data={data!} theme={theme} />
          )}
        </main>

        {/* Notes History & Search Modal */}
        <NotesHistoryModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          churchName={church.name}
          churchSlug={church.slug}
          landingConfig={landing}
        />

        {/* Global First-Time / Announcement Attendee Modal */}
        {!isEmbedPreview && (
          <FirstTimeModal
            churchName={church.name}
            churchSlug={church.slug}
            config={landing.welcome_modal}
          />
        )}

        {/* Footer */}
        {(landing.links?.length || landing.footer_text) && (
          <footer className="mt-8 space-y-3 text-center">
            {landing.links && landing.links.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {landing.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx('rounded-full px-3 py-1.5 text-sm font-medium', landing.theme === 'brand' ? 'bg-white/15 text-white' : theme.chip)}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
            {landing.footer_text && <p className="text-xs opacity-70">{landing.footer_text}</p>}
          </footer>
        )}
      </div>
    </div>
  );
}

function Idle({ data, theme }: { data: LivePageData; theme: ThemeTokens }) {
  const { church, next_service, live } = data;
  const landing = church.landing || {};
  return (
    <article className={cx('overflow-hidden rounded-3xl shadow-xl animate-fade-up', theme.card)}>
      {landing.hero_image_url && <img src={landing.hero_image_url} alt="" className="aspect-[16/9] w-full object-cover" />}
      <div className="space-y-4 p-6 sm:p-8">
        <h1 className="text-2xl font-bold sm:text-3xl">
          {live ? landing.idle_title || landing.welcome_title || 'Welcome!' : landing.welcome_title || `Welcome to ${church.name}`}
        </h1>
        <Markdown className={theme.muted}>{live ? landing.idle_message || landing.welcome_message : landing.welcome_message}</Markdown>
        {!live && next_service && landing.show_next_service !== false && (
          <div className={cx('flex items-center gap-3 rounded-2xl p-4', theme.chip)}>
            <CalendarClock className="h-6 w-6 shrink-0 text-brand" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Next service</p>
              <p className="font-semibold">{next_service.name}</p>
              <p className="text-sm opacity-80">
                {formatInZone(next_service.starts_at, church.timezone, { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
